import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import { PopupOverlay } from "@/components/Map/Overlays";

type VertexPopupProps = {
  position: [number, number];
  coordX: string;
  coordY: string;
  srsCode: string;
  onCoordXChange: (value: string) => void;
  onCoordYChange: (value: string) => void;
  onCommit: (closeAfterCommit?: boolean) => void;
  onCancel: () => void;
  onDeleteVertex: () => void;
  canDeleteVertex: boolean;
};

const VertexPopup = ({
  position,
  coordX,
  coordY,
  srsCode,
  onCoordXChange,
  onCoordYChange,
  onCommit,
  onCancel,
  onDeleteVertex,
  canDeleteVertex,
}: VertexPopupProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onGlobalPopupKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === "Enter") {
        evt.preventDefault();
        onCommit();
      } else if (evt.key === "Escape") {
        evt.preventDefault();
        onCancel();
      }
    };

    document.addEventListener("keydown", onGlobalPopupKeyDown);
    return () => {
      document.removeEventListener("keydown", onGlobalPopupKeyDown);
    };
  }, [onCommit, onCancel]);

  return (
    <PopupOverlay id="feature-map-vertex-popup" position={position} onClose={onCancel} autoPan>
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={() => onCommit(true)}
      >
        <Box sx={{ p: 1, minWidth: 230, bgcolor: "white", borderRadius: 0.5, border: "1px solid #ccc" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("map:common.geometry_point")}
            </Typography>
            <IconButton size="small" onClick={onCancel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack spacing={1}>
            <TextField
              size="small"
              label="X"
              value={coordX}
              onChange={(evt) => onCoordXChange(evt.target.value)}
              onKeyDown={(evt) => {
                if (evt.key === "Enter") {
                  evt.preventDefault();
                  evt.stopPropagation();
                  onCommit();
                } else if (evt.key === "Escape") {
                  evt.preventDefault();
                  evt.stopPropagation();
                  onCancel();
                }
              }}
            />
            <TextField
              size="small"
              label="Y"
              value={coordY}
              onChange={(evt) => onCoordYChange(evt.target.value)}
              onKeyDown={(evt) => {
                if (evt.key === "Enter") {
                  evt.preventDefault();
                  evt.stopPropagation();
                  onCommit();
                } else if (evt.key === "Escape") {
                  evt.preventDefault();
                  evt.stopPropagation();
                  onCancel();
                }
              }}
            />
            <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5 }}>
              {t(
                "map:feature_map.vertex_popup_enter_hint"
                // "Pritisnite Enter kada ste uredili koordinatu."
              )}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.25, display: "block" }}>
              {`${t("map:common.srs.text")}: ${t(`map:common.srs.${srsCode}`, srsCode)}`}
            </Typography>
            <Button
              variant="text"
              color="error"
              size="small"
              disabled={!canDeleteVertex}
              onClick={onDeleteVertex}
              sx={{ mt: 0.5, alignSelf: "flex-start" }}
            >
              {t("map:buttons.delete_vertex")}
            </Button>
          </Stack>
        </Box>
      </ClickAwayListener>
    </PopupOverlay>
  );
};

export default VertexPopup;
